import { useState, useEffect } from 'react';
import Metadata from '../components/Metadata';
import Lightbox from '../components/Lightbox';
import { fetchGallery } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Galleri() {
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadProjects() {
      const data = await fetchGallery();
      setProjects(data);
    }
    loadProjects();
  }, []);

  // Extract unique categories
  const categories = ['Alle', ...new Set(projects.map((p) => p.category))];

  // Filter projects based on active category
  const filteredProjects =
    activeCategory === 'Alle'
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  return (
    <>
      <Metadata
        title="Referencer - K. Pheiffer Construction"
        description="Se tidligere projekter og referencer udført af K. Pheiffer Construction."
      />

      {/* Page Header */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.05),transparent_40%)]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
            Vores arbejde
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mt-2">
            Galleri & Referencer
          </h1>
          <p className="mt-4 text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            Gå på opdagelse i vores tidligere projekter og se kvaliteten af vores håndværk. Klik på et billede for at se det i stor visning.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Category Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded text-sm font-semibold tracking-wide transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-900/10'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Ingen projekter fundet i denne kategori.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  onClick={() => setLightboxIndex(index)}
                  className="group cursor-pointer flex flex-col bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={getOptimizedImageUrl(project.image[0], 800, 90)}
                      alt={project.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="h-12 w-12 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                        <i className="fa-regular fa-magnifying-glass-plus text-lg" />
                      </div>
                    </div>
                    <span className="absolute top-4 left-4 text-xs font-bold text-white bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
                      {project.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="mt-2 text-slate-500 text-xs sm:text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={filteredProjects}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev === 0 ? filteredProjects.length - 1 : prev - 1))}
          onNext={() => setLightboxIndex((prev) => (prev === filteredProjects.length - 1 ? 0 : prev + 1))}
        />
      )}
    </>
  );
}
